import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMember } from "../member/MemberContext";
import { useStaffIdentity } from "../staff/StaffIdentityProvider";
import { useStaffUserService } from "../../services/members/StaffUserServiceProvider";
import {
  AdminPermissions,
  ClubOwnerPermissions,
  StaffPermissions,
} from "../../model/auth/Permissions";

/**
 * How long to wait for `/dashboard/me` before letting the screen render anyway. The identity
 * only decides the LANDING filter, so a slow answer must not hold a CRM hostage — but a late
 * answer is still honoured when it arrives (see the effect below).
 */
const IDENTITY_TIMEOUT_MS = 2000;

/**
 * The `?mine` URL contract: `1` = My Leads, `0` = All Leads, anything else = not chosen.
 *
 * Strict on purpose. Treating any present value as a choice means a typo — or `?mine=true`,
 * which is the spelling the leads *API* uses — would mark the filter "chosen" while leaving it
 * undecided, and the screen would wait forever for a decision that can no longer be made.
 */
export const parseMineParam = (value: string | null): boolean | null =>
  value === "1" ? true : value === "0" ? false : null;

/**
 * Where someone lands when the URL hasn't already said.
 *
 * Picking a name in the header's staff picker wins over everything else: on a shared
 * reception machine that selection IS the answer to "whose leads am I looking at", so the
 * CRMs open on that person's leads whatever tier the login holds. A manager who signs the
 * desk in as someone came to work that person's list, not the whole pipeline.
 *
 * `pickedStaffId` must be null whenever the identity came from the login instead — see the
 * caller. A login-linked user has no picker on screen, so a leftover selection from a
 * previous shift is not something they said.
 *
 * Failing that, non-managers start on their own leads and everyone else on the full list.
 * "No staff identity" also means All Leads — with nothing linked, nothing is "mine", and My
 * Leads would be a permanently empty screen.
 */
export const decideLandingFilter = (
  canViewAllLeads: boolean,
  mineStaffId: number | null,
  pickedStaffId: number | null
): boolean => pickedStaffId != null || (!canViewAllLeads && mineStaffId != null);

/**
 * FORT-376: the My Leads / All Leads filter shared by BOTH CRMs — Membership retention and
 * Business Development.
 *
 * The two screens narrow by different mechanisms (Membership passes `mine` to the API; BD
 * reads the whole tenant set and filters in the browser, per the FORT-362 ruling), but the
 * QUESTION is identical: whose leads should this person be looking at on arrival, and what did
 * they choose afterwards. Keeping that in one place is the point — two hand-maintained copies
 * of a tier rule drift, and a staff member seeing their own leads in one CRM and the whole
 * pipeline in the other is exactly the confusion this was asked to remove.
 *
 *   | Caller                                    | Staff identity       | Lands on  |
 *   |-------------------------------------------|----------------------|-----------|
 *   | Anyone                                    | picked in the header | My Leads  |
 *   | Non-manager                               | linked to their login| My Leads  |
 *   | Non-manager                               | none linked          | All Leads |
 *   | Manager / club owner / club admin / admin | from login, or none  | All Leads |
 *
 * `?mine=1` / `?mine=0` wins on arrival — Staff Daily's deep link, and a remembered choice.
 * Only picking a new name in the header clears it, because that is a different person asking.
 *
 * NOT an access boundary. Both APIs still serve every lead to anyone who asks and the toggle
 * stays available to everyone; this decides only where you START. The client was explicit that
 * a staff user must still be able to select All Leads.
 */
export interface LeadOwnershipFilter {
  /**
   * `null` while undecided — the tier and the staff identity both arrive after first render.
   * Callers must treat this as "still loading" rather than as All Leads: fetching on it fires
   * once per scope, and filtering on it blinks the list.
   */
  showMineOnly: boolean | null;
  /** Records an explicit user choice: updates the filter AND the URL. */
  chooseFilter: (mineOnly: boolean) => void;
  /**
   * The staff id "mine" means, for screens that filter in the browser (BD). Null when nobody
   * is linked — which is also why such a caller lands on All Leads.
   */
  mineStaffId: number | null;
  /**
   * Whether the identity lookup has finished, which `mineStaffId` alone cannot say — null is
   * also the answer for "nobody". Any control whose behaviour depends on who is asking must
   * wait for this, or it renders one action and swaps to another under a moving cursor
   * (FORT-374's sign-off vs log-contact hazard).
   */
  identitySettled: boolean;
}

export function useLeadOwnershipFilter(): LeadOwnershipFilter {
  const { permissions, isLoading: permissionsLoading } = useMember();
  const { selectedStaffId } = useStaffIdentity();
  const staffUserService = useStaffUserService();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showMineOnly, setShowMineOnly] = useState<boolean | null>(() =>
    parseMineParam(searchParams.get("mine"))
  );
  // Only an explicit choice writes the URL, and only a PARSEABLE value counts as one — a
  // defaulted My Leads must not stamp `?mine=1` (it would author the very deep link it sits
  // behind), and an unparseable `?mine=` must not mark the filter decided.
  const [filterChosenByUser, setFilterChosenByUser] = useState(
    () => parseMineParam(searchParams.get("mine")) !== null
  );

  /**
   * Who lands on All Leads.
   *
   * `members:view-all-leads` is the purpose-built permission and is honoured first;
   * `staff:tasks:create` is what lets this ship with no new Auth0 grant, being the manager
   * marker already used for Work Allocation.
   *
   * The legacy `members:manage-followups` is deliberately absent even though it opens both
   * CRMs: EVERY reception account holds it (it was the old CRM gate — see auth0/README.md), so
   * honouring it would land exactly the tier this default exists for back on the full pipeline
   * and make the whole thing a no-op. A club owner holding only the legacy permission
   * therefore starts on My Leads and switches; granting them `members:view-all-leads` in Auth0
   * fixes that with no code change.
   */
  const canViewAllLeads = useMemo(
    () =>
      (permissions?.permissions ?? []).some(
        (p) =>
          p.name === ClubOwnerPermissions.ViewAllLeads ||
          p.name === StaffPermissions.CreateTasks ||
          p.name === AdminPermissions.ClubOwner ||
          p.name === AdminPermissions.ClubAdmin ||
          p.name === AdminPermissions.FortiusAdmin
      ),
    [permissions]
  );

  /**
   * Which staff member is asking, or null.
   *
   * Resolved through `/dashboard/me` rather than the picker alone, because that is the API's
   * own precedence — a verified login email wins, the shared-device picker is the fallback —
   * and anyone whose email is linked to a staff row never touches the picker.
   */
  const [mineStaffId, setMineStaffId] = useState<number | null>(null);
  const [identitySettled, setIdentitySettled] = useState(false);
  /**
   * Whether that identity came from the login itself rather than the header picker.
   *
   * This is what separates a genuine pick from a leftover: `selectedStaffId` is device-scoped
   * localStorage that outlives the session (it is only cleared on an explicit logout, and the
   * picker only reaps it when the API answers "nobody"). Without this flag, a name someone
   * picked at the desk yesterday would silently default a login-linked manager to My Leads
   * today — with no picker on screen to explain why, and while the API ignores the header
   * entirely for them, because a verified email always wins.
   */
  const [identityFromLogin, setIdentityFromLogin] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setIdentitySettled(false);
    // The timeout releases the GATE only; it does not decide the answer. A response that
    // arrives late still wins, because racing the promise away would strand `mineStaffId` at
    // null for the life of the screen — leaving My Leads empty and the user's own rows
    // offering the un-audited action forever.
    const timer = setTimeout(() => {
      if (!cancelled) setIdentitySettled(true);
    }, IDENTITY_TIMEOUT_MS);

    staffUserService
      .getCurrentStaff()
      // getCurrentStaff resolves to undefined on a failed request rather than rejecting, so
      // both branches land here; a failure and "nobody linked" mean the same thing to us.
      .catch(() => null)
      .then((current) => {
        if (cancelled) return;
        clearTimeout(timer);
        // Falls back to the shared-device picker: if the lookup failed, the person standing at
        // the desk has still told us who they are.
        setMineStaffId(current?.staff?.id ?? selectedStaffId ?? null);
        // A failed lookup is not a login-linked identity — leave the pick honoured rather than
        // discarding the only answer we have.
        setIdentityFromLogin(current?.fromLogin === true);
        setIdentitySettled(true);
      });

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // selectedStaffId: on a shared machine nobody has picked at first paint, so the landing
    // default resolves to All Leads. Picking a name in the header must re-run this, or the
    // "a staff user is selected → My Leads" rule never fires in the flow the picker exists for.
  }, [staffUserService, selectedStaffId]);

  /**
   * The selection, but only where it is actually the answer to "who is asking?".
   *
   * A login-linked identity outranks the picker everywhere else — in the API, and in the
   * picker's own decision not to render — so it has to outrank a leftover `currentStaffId`
   * here too.
   */
  const pickedStaffId = identityFromLogin ? null : selectedStaffId;

  /**
   * Picking a name in the header re-opens the question.
   *
   * Someone new is now at the desk, so the previous person's "show me everything" must not
   * carry over — the pick is itself an answer to "whose leads?", and honouring a stale choice
   * would leave them staring at the whole pipeline with their own name in the header. Clearing
   * `filterChosenByUser` hands the decision back to the landing rule below rather than
   * hard-setting My Leads here, so one place still owns the answer.
   *
   * `?mine` goes with it: the URL is what a remount (navigating between the two CRMs) reads
   * back, so leaving a stale `mine=0` behind would quietly reinstate the choice we just
   * dropped. A cleared selection (`null` — the picker reaping a deactivated staff row) is not
   * a pick and changes nothing.
   *
   * The ref starts at the mounted-with value, so arriving on a machine where someone had
   * already picked is not treated as a change — an explicit `?mine=0` deep link still wins on
   * arrival, exactly as before.
   */
  const previousPickedStaffId = useRef(selectedStaffId);
  useEffect(() => {
    const previous = previousPickedStaffId.current;
    previousPickedStaffId.current = selectedStaffId;
    if (identityFromLogin) return;
    if (selectedStaffId == null || selectedStaffId === previous) return;

    // Answer "mine" from the pick straight away rather than waiting on /dashboard/me. The
    // identity effect above has just re-armed its 2s timeout, and that timeout releases the
    // gate without deciding anything — so the landing rule could otherwise commit My Leads
    // while this still held the PREVIOUS person's id (or none at all, which the BD CRM
    // renders as an empty list). The response still overwrites this when it lands.
    setMineStaffId(selectedStaffId);
    setFilterChosenByUser(false);
    setShowMineOnly(null);
    // Only when there is something to clear: a defaulted filter never writes the URL, so the
    // common case has no `mine` at all and would otherwise take a pointless navigation.
    if (searchParams.has("mine")) {
      setSearchParams(
        (params) => {
          const next = new URLSearchParams(params);
          next.delete("mine");
          return next;
        },
        { replace: true }
      );
    }
  }, [selectedStaffId, identityFromLogin, searchParams, setSearchParams]);

  // Decide the landing filter once both inputs have settled. Skipped entirely once the user has
  // chosen, so it can never fight them.
  useEffect(() => {
    if (filterChosenByUser) return;
    if (permissionsLoading || !identitySettled) return;
    setShowMineOnly(decideLandingFilter(canViewAllLeads, mineStaffId, pickedStaffId));
  }, [
    filterChosenByUser,
    permissionsLoading,
    identitySettled,
    canViewAllLeads,
    mineStaffId,
    pickedStaffId,
  ]);

  const chooseFilter = useCallback((mineOnly: boolean) => {
    setFilterChosenByUser(true);
    setShowMineOnly(mineOnly);
  }, []);

  // Mirror an explicit choice into the URL so it survives a refresh or a remount. `mine=0` is
  // written as well as `mine=1`: without a representable "all", the landing default would
  // quietly overrule the choice the client specifically asked staff to be able to make.
  useEffect(() => {
    if (!filterChosenByUser || showMineOnly === null) return;
    const wanted = showMineOnly ? "1" : "0";
    if (searchParams.get("mine") === wanted) return;
    const next = new URLSearchParams(searchParams);
    next.set("mine", wanted);
    setSearchParams(next, { replace: true });
  }, [filterChosenByUser, showMineOnly, searchParams, setSearchParams]);

  return { showMineOnly, chooseFilter, mineStaffId, identitySettled };
}
